import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Eye, EyeOff, ShieldCheck, Mail, Lock, Server, Globe, Zap, Shield, CheckCircle2 } from 'lucide-react';
import AdminPage from './pages/admin/AdminPage.jsx';
import { API_BASE } from './config';

/* ─────────────────────────────────────────────────────────────
   PREMIUM LIGHT THEME — Stripe / Linear / Vercel style
───────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ─────────────────────────────
     1. LAYERED GRADIENT BACKGROUND with slow animation
  ───────────────────────────────*/
  body, html { height: 100%; }

  .xe-page {
    min-height: 100vh;
    font-family: 'Inter', system-ui, sans-serif;
    display: flex;
    position: relative;
    overflow: hidden;

    /* 6. Better Color Contrast & Background */
    background:
      radial-gradient(circle at 20% 30%, rgba(79, 124, 255, 0.15), transparent 45%),
      radial-gradient(circle at 80% 75%, rgba(99, 102, 241, 0.12), transparent 45%),
      linear-gradient(135deg, #f8fafc, #eef2ff);

    background-size: 200% 200%;
    animation: bgDrift 30s linear infinite alternate;
  }

  /* 9. Slow gradient drift */
  @keyframes bgDrift {
    0%   { background-position: 0% 50%;   }
    100% { background-position: 100% 50%; }
  }

  /* 2. Subtle grid overlay */
  .xe-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(65, 105, 225, 0.045) 1px, transparent 1px),
      linear-gradient(90deg, rgba(65, 105, 225, 0.045) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
    z-index: 0;
  }

  /* ─────────────────────────────
     LAYOUT — two columns
  ───────────────────────────────*/
  .xe-layout {
    position: relative;
    z-index: 1;
    width: 100%;
    display: flex;
    align-items: stretch;
  }

  /* ─────────────────────────────
     LEFT — Branding
  ───────────────────────────────*/
  .xe-left {
    width: 52%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 52px 72px;
    position: relative;
  }

  /* Soft right-edge fade so it blends into right panel */
  .xe-left::after {
    content: '';
    position: absolute;
    right: 0; top: 0; bottom: 0;
    width: 120px;
    background: linear-gradient(to right, transparent, rgba(240, 244, 255, 0.7));
    pointer-events: none;
  }

  /* Logo */
  .xe-logo-row { display: flex; align-items: center; gap: 13px; }
  .xe-logo-mark {
    width: 46px; height: 46px; border-radius: 13px;
    background: rgba(255,255,255,0.9);
    border: 1.5px solid rgba(79, 124, 255, 0.2);
    box-shadow: 0 4px 16px rgba(79, 124, 255, 0.15);
    overflow: hidden; flex-shrink: 0;
    transition: box-shadow 0.3s ease;
  }
  .xe-logo-mark:hover { box-shadow: 0 8px 28px rgba(79, 124, 255, 0.28); }
  .xe-logo-mark img { width: 100%; height: 100%; object-fit: cover; }
  .xe-logo-text { font-size: 19px; font-weight: 800; color: #0f172a; letter-spacing: -0.025em; }
  .xe-logo-sub  { font-size: 11px; font-weight: 700; color: #5b7cff; letter-spacing: 0.22em; text-transform: uppercase; opacity: 0.85; display: block; margin-top: 2px; }

  /* Hero text block */
  .xe-hero { margin-top: 12px; }

  /* 8️⃣ Premium SaaS-like status pill */
  .xe-live-pill {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(79, 124, 255, 0.08);
    border: 1px solid rgba(79, 124, 255, 0.18);
    border-radius: 999px; padding: 6px 16px;
    margin-bottom: 30px;
    box-shadow: 0 2px 8px rgba(79, 124, 255, 0.08);
  }
  .xe-live-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
    animation: livePulse 2.4s ease infinite;
  }
  @keyframes livePulse {
    0%   { box-shadow: 0 0 0 0   rgba(34, 197, 94, 0.5); }
    70%  { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0);   }
    100% { box-shadow: 0 0 0 0   rgba(34, 197, 94, 0);   }
  }
  .xe-live-txt { font-size: 12px; font-weight: 700; color: #5b7cff; letter-spacing: 0.18em; text-transform: uppercase; }

  /* 7️⃣ Typography upgrade */
  .xe-headline {
    font-size: 64px; font-weight: 800;
    color: #0f172a; letter-spacing: -0.03em;
    line-height: 1.05; margin-bottom: 20px;
  }
  .xe-headline span { color: #5b7cff; }
  .xe-subtitle {
    font-size: 18px; font-weight: 400;
    color: #64748b; line-height: 1.65;
    max-width: 390px; margin-bottom: 48px;
  }

  /* 6️⃣ Feature cards — premium glass with hover lift */
  .xe-features { display: flex; flex-direction: column; gap: 12px; }
  .xe-feat-card {
    display: flex; align-items: center; gap: 18px;
    padding: 16px 20px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 18px;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(0,0,0,0.04);
    cursor: default;
    transition:
      transform  0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease,
      border-color 0.25s ease,
      background  0.25s ease;
  }
  .xe-feat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(0,0,0,0.05);
    border-color: rgba(91, 124, 255, 0.2);
    background: rgba(255,255,255,0.98);
  }
  .xe-feat-icon {
    width: 42px; height: 42px; flex-shrink: 0;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(79,124,255,0.12), rgba(79,124,255,0.05));
    border: 1px solid rgba(79,124,255,0.18);
    color: #5b7cff;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
  }
  .xe-feat-card:hover .xe-feat-icon {
    transform: scale(1.1);
    box-shadow: 0 0 14px rgba(79, 124, 255, 0.22);
  }
  .xe-feat-name { font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
  .xe-feat-desc { font-size: 13px; color: #94a3b8; }

  /* Bottom status */
  .xe-status { display: flex; align-items: center; gap: 8px; }
  .xe-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 8px rgba(34,197,94,0.7); flex-shrink: 0; }
  .xe-status-txt { font-size: 12px; font-weight: 700; color: #94a3b8; letter-spacing: 0.14em; text-transform: uppercase; }

  /* ─────────────────────────────
     RIGHT — Login Panel
  ───────────────────────────────*/
  .xe-right {
    flex: 1;
    display: flex; align-items: center; justify-content: center;
    padding: 52px 60px 52px 24px;
    position: relative;
  }

  /* 1. Light Diffusion Glow (Powerful Trick) */
  .xe-right::before {
    content: "";
    position: absolute;
    width: 700px;
    height: 700px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      circle,
      rgba(79, 124, 255, 0.25),
      transparent 60%
    );
    filter: blur(120px);
    z-index: -1;
  }

  /* Subtle vertical divider */
  .xe-right::after {
    content: '';
    position: absolute;
    left: 0; top: 8%; bottom: 8%; width: 1px;
    background: linear-gradient(to bottom, transparent, rgba(79, 124, 255, 0.15), transparent);
  }

  /* 4. Subtle Floating Motion & 5. Premium Shadow Layering */
  .xe-card {
    width: 100%; max-width: 420px;
    position: relative;
    background: #ffffff;
    border-radius: 24px;
    border: 1px solid rgba(15, 23, 42, 0.05);
    padding: 48px 42px;
    box-shadow:
      0 30px 60px rgba(15, 23, 42, 0.12),
      0 10px 25px rgba(15, 23, 42, 0.08),
      0 2px  6px rgba(15, 23, 42, 0.05);
    animation: 
      cardRise 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both,
      float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%   { transform: translateY(0px); }
    50%  { transform: translateY(-6px); }
    100% { transform: translateY(0px); }
  }

  @keyframes cardRise {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1);     }
  }

  /* Luminous top-edge highlight */
  .xe-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.0) 60%
    );
    pointer-events: none;
  }

  /* Card header icon row */
  .xe-card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 26px; }
  .xe-card-icon-box {
    width: 34px; height: 34px; border-radius: 10px;
    background: rgba(79, 124, 255, 0.09);
    border: 1px solid rgba(79, 124, 255, 0.2);
    display: flex; align-items: center; justify-content: center;
    color: #5b7cff; flex-shrink: 0;
  }
  .xe-card-divider {
    flex: 1; height: 1px;
    background: linear-gradient(to right, rgba(79, 124, 255, 0.20), transparent);
  }

  .xe-card-title { font-size: 28px; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; margin-bottom: 6px; }
  .xe-card-sub   { font-size: 13px; color: #64748b; margin-bottom: 28px; }

  /* 6. Input fields */
  .xe-field { margin-bottom: 18px; }
  .xe-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 700; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.16em;
    margin-bottom: 8px;
  }
  .xe-label svg { color: #5b7cff; opacity: 0.8; }
  .xe-input-wrap { position: relative; }
  .xe-input {
    width: 100%;
    padding: 14px 46px 14px 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-family: 'Inter', sans-serif;
    font-size: 14px; color: #0f172a;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    caret-color: #5b7cff;
  }
  .xe-input::placeholder { color: #cbd5e1; }
  .xe-input:focus {
    border-color: #5b7cff;
    box-shadow: 0 0 0 4px rgba(91, 124, 255, 0.15);
  }
  .xe-eye {
    position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer; color: #cbd5e1;
    display: flex; align-items: center;
    transition: color 0.15s;
  }
  .xe-eye:hover { color: #5b7cff; }

  /* 4️⃣ + 5️⃣ Premium gradient button */
  .xe-btn {
    width: 100%; margin-top: 10px; padding: 16px;
    border: none; border-radius: 12px; cursor: pointer;
    font-family: 'Inter', sans-serif; font-size: 14px;
    font-weight: 700; color: #fff; letter-spacing: 0.02em;
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #5b7cff 0%, #3f63f0 100%);
    box-shadow: 0 12px 30px rgba(79, 124, 255, 0.45), 0 4px 10px rgba(0,0,0,0.10);
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.22s ease;
  }
  .xe-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(79, 124, 255, 0.52), 0 6px 16px rgba(0,0,0,0.12);
  }
  .xe-btn:active:not(:disabled) { transform: scale(0.99); }
  .xe-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  /* shimmer sweep on hover */
  .xe-btn::after {
    content: ''; position: absolute;
    top: 0; left: -120%; width: 60%; height: 100%;
    background: linear-gradient(to right, transparent, rgba(255,255,255,0.22), transparent);
    transform: skewX(-20deg);
    transition: left 0.55s ease;
  }
  .xe-btn:hover:not(:disabled)::after { left: 160%; }
  .xe-btn-inner { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; gap: 8px; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .xe-spin {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
    border-radius: 50%; animation: spin 0.75s linear infinite;
  }

  /* Error */
  .xe-error {
    display: flex; align-items: flex-start; gap: 10px;
    background: rgba(254, 226, 226, 0.8);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 12px; padding: 12px 14px; margin-bottom: 22px;
  }
  .xe-err-dot { width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0; margin-top: 4px; }
  .xe-err-txt { font-size: 12px; font-weight: 500; color: #b91c1c; }

  /* Security badges */
  .xe-security {
    display: flex; justify-content: center; flex-wrap: wrap;
    gap: 14px; margin-top: 24px; padding-top: 22px;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
  }
  .xe-sec-badge {
    display: flex; align-items: center; gap: 5px;
    font-size: 12px; font-weight: 700; color: #94a3b8; letter-spacing: 0.04em;
  }
  .xe-sec-badge svg { color: #5b7cff; filter: drop-shadow(0 0 4px rgba(91,124,255,0.45)); }

  /* 🔟 Grain noise texture — Stripe / Apple / Linear secret */
  .xe-page::after {
    content: '';
    position: fixed; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.08'/%3E%3C/svg%3E");
    opacity: 0.028;
    pointer-events: none;
    z-index: 9999;
  }

  .xe-footer {
    text-align: center; margin-top: 18px;
    font-size: 12px; font-weight: 600; color: #cbd5e1;
    letter-spacing: 0.16em; text-transform: uppercase;
  }

  /* ─────────────────────────────
     MOBILE
  ───────────────────────────────*/
  @media (max-width: 920px) {
    .xe-layout { flex-direction: column; min-height: 100vh; }
    .xe-left { display: none; }
    .xe-right { flex: 1; padding: 40px 24px; }
    .xe-right::after { display: none; }
    .xe-card { max-width: 100%; }
  }
`;

/* ─────────────────────────────────────────────────────────────
   LOGIN COMPONENT
───────────────────────────────────────────────────────────── */
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const loginUrl = mfaRequired 
      ? `${API_BASE}/auth/2fa/login` 
      : `${API_BASE}/auth/login`;
      
    const payload = mfaRequired 
      ? { identity: email, code: mfaCode } 
      : { mail: email, password };

    console.log(`📡 Attempting ${mfaRequired ? 'MFA' : 'Standard'} login at: ${loginUrl}`);
    
    try {
      const res = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      console.log(`🔄 Response Status: ${res.status}`);
      const data = await res.json();
      
      if (res.ok) {
        if (data.mfaRequired) {
          setMfaRequired(true);
          setLoading(false);
          return;
        }
        
        if (data.token) {
          const decoded = jwtDecode(data.token);
          if (decoded.role === 'ADMIN' || decoded.role === 'ROLE_ADMIN') {
            onLogin(email, data.token);
          } else {
            setError('Access denied. Administrator privileges required.');
          }
        } else {
          setError('Invalid server response: No token received.');
        }
      } else {
        setError(data.message || 'Sign in failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ Connection Error:', err);
      setError('Unable to connect. Please check your network.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <Server size={16} />, label: 'Always Available', desc: 'Zero-downtime infrastructure' },
    { icon: <Globe size={16} />, label: 'Worldwide Delivery', desc: 'Reach anyone, anywhere' },
    { icon: <Zap size={16} />, label: 'Instant Visibility', desc: 'See everything in real time' },
  ];

  return (
    <>
      <style>{STYLES}</style>

      {/* Grid overlay */}
      <div className="xe-grid" />

      <div className="xe-page">
        <div className="xe-layout">

          {/* ─── LEFT — Branding ──────────────────────────────── */}
          <div className="xe-left">

            {/* Logo */}
            <div className="xe-logo-row">
              <div className="xe-logo-mark">
                <img src="/assets/xemail_logo.png" alt="XeMail" />
              </div>
              <div>
                <div className="xe-logo-text">XeMail</div>
                <span className="xe-logo-sub">Enterprise Platform</span>
              </div>
            </div>

            {/* Hero */}
            <div className="xe-hero">
              <div className="xe-live-pill">
                <span className="xe-live-dot" />
                <span className="xe-live-txt">All Systems Online</span>
              </div>

              <h1 className="xe-headline">
                Xe<span>Mail</span>
              </h1>

              <p className="xe-subtitle">
                Enterprise email infrastructure for modern teams.
                Secure. Reliable. Always on.
              </p>

              <div className="xe-features">
                {features.map((f, i) => (
                  <div className="xe-feat-card" key={i}>
                    <div className="xe-feat-icon">{f.icon}</div>
                    <div>
                      <div className="xe-feat-name">{f.label}</div>
                      <div className="xe-feat-desc">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status foot */}
            <div className="xe-status">
              <span className="xe-status-dot" />
              <span className="xe-status-txt">Secure · Encrypted · Always On</span>
            </div>

          </div>

          {/* ─── RIGHT — Login ────────────────────────────────── */}
          <div className="xe-right">
            <div className="xe-card">

              {/* Card header */}
              <div className="xe-card-header">
                <div className="xe-card-icon-box">
                  <ShieldCheck size={15} />
                </div>
                <div className="xe-card-divider" />
              </div>

              <div className="xe-card-title">{mfaRequired ? 'Security Check' : 'Welcome back'}</div>
              <div className="xe-card-sub">
                {mfaRequired 
                  ? 'Please enter the code from your authenticator app.' 
                  : 'Sign in to manage your XeMail platform.'}
              </div>

              {/* Error */}
              {error && (
                <div className="xe-error">
                  <span className="xe-err-dot" />
                  <span className="xe-err-txt">{error}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {!mfaRequired ? (
                  <>
                    <div className="xe-field">
                      <label className="xe-label">
                        <Mail size={10} /> Email Address
                      </label>
                      <div className="xe-input-wrap">
                        <input
                          className="xe-input"
                          type="email" required
                          placeholder="admin@xemail.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          style={{ paddingRight: 16 }}
                        />
                      </div>
                    </div>

                    <div className="xe-field">
                      <label className="xe-label">
                        <Lock size={10} /> Password
                      </label>
                      <div className="xe-input-wrap">
                        <input
                          className="xe-input"
                          type={showPwd ? 'text' : 'password'}
                          required
                          placeholder="Enter your password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="xe-eye"
                          onClick={() => setShowPwd(v => !v)}
                        >
                          {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="xe-field">
                    <label className="xe-label">
                      <ShieldCheck size={10} /> Authenticator Code
                    </label>
                    <div className="xe-input-wrap">
                      <input
                        className="xe-input"
                        type="text" required
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                        value={mfaCode}
                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                  </div>
                )}

                <button className="xe-btn" type="submit" disabled={loading}>
                  <span className="xe-btn-inner">
                    {loading ? (
                      <><span className="xe-spin" /> {mfaRequired ? 'Verifying…' : 'Signing in…'}</>
                    ) : (
                      <><ShieldCheck size={15} strokeWidth={2.5} /> {mfaRequired ? 'Verify Code' : 'Sign In'}</>
                    )}
                  </span>
                </button>
                
                {mfaRequired && (
                  <button 
                    type="button"
                    onClick={() => { setMfaRequired(false); setError(''); }}
                    className="xe-footer"
                    style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', marginTop: 15 }}
                  >
                    Back to login
                  </button>
                )}
              </form>

              {/* Security badges */}
              <div className="xe-security">
                <div className="xe-sec-badge"><Shield size={11} /> Secure Login</div>
                <div className="xe-sec-badge"><ShieldCheck size={11} /> Enterprise Protection</div>
                <div className="xe-sec-badge"><CheckCircle2 size={11} /> Encrypted Transfer</div>
              </div>

              <div className="xe-footer">© 2026 XeMail · Enterprise Platform</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────
   APP ROOT
───────────────────────────────────────────────────────────── */
const App = () => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('xemail_user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = (email, token) => {
    const u = { email, token, role: 'ADMIN' };
    setUser(u);
    localStorage.setItem('xemail_user', JSON.stringify(u));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('xemail_user');
  };

  if (!user) return <Login onLogin={handleLogin} />;
  return <AdminPage onLogout={handleLogout} />;
};

export default App;
